// projectFloorPlanModel.js

/**
 * Pure helpers for project floor-plan layout documents (Floor → Room).
 */

export const PROJECT_FLOOR_PLAN_VERSION = 1;

/** Fixed square stage for the three-column floor-plan layout. */
export const DEFAULT_CANVAS_WIDTH = 480;
export const DEFAULT_CANVAS_HEIGHT = 480;

export const DEFAULT_ROOM_WIDTH = 140;
export const DEFAULT_ROOM_HEIGHT = 100;

const FLOOR_NAME_SEEDS = Object.freeze([
  'Main Floor',
  'Second Floor',
  'Third Floor',
  'Fourth Floor',
  'Fifth Floor',
]);

/**
 * createLayoutId
 *
 * @param {string} prefix
 * @returns {string}
 */
export function createLayoutId(prefix = 'id') {
  const safePrefix = String(prefix || 'id').trim() || 'id';
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${safePrefix}_${crypto.randomUUID().replace(/-/g, '')}`;
  }
  return `${safePrefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * normalizeFloorPlan
 *
 * @param {object|null|undefined} raw
 * @param {{ baseKey?: string, projectKey?: string }} [identity]
 * @returns {object}
 */
export function normalizeFloorPlan(raw, identity = {}) {
  const baseKey = String(identity.baseKey || raw?.baseKey || '').trim();
  const projectKey = String(identity.projectKey || raw?.projectKey || raw?.key || '').trim();
  const floorsIn = Array.isArray(raw?.floors) ? raw.floors : [];

  const floors = floorsIn.map((floor, index) => normalizeFloor(floor, index)).filter(Boolean);

  return {
    key: projectKey,
    baseKey,
    projectKey,
    version: Number(raw?.version) || PROJECT_FLOOR_PLAN_VERSION,
    floors,
    canvasWidth: Math.max(320, Number(raw?.canvasWidth) || DEFAULT_CANVAS_WIDTH),
    canvasHeight: Math.max(240, Number(raw?.canvasHeight) || DEFAULT_CANVAS_HEIGHT),
  };
}

/**
 * createEmptyFloorPlan
 *
 * @param {{ baseKey: string, projectKey: string }} params
 * @returns {object}
 */
export function createEmptyFloorPlan({ baseKey, projectKey }) {
  return normalizeFloorPlan({
    floors: [],
    canvasWidth: DEFAULT_CANVAS_WIDTH,
    canvasHeight: DEFAULT_CANVAS_HEIGHT,
  }, { baseKey, projectKey });
}

/**
 * createDefaultFloorPlan
 *
 * Seeds Main Floor for first edit.
 *
 * @param {{ baseKey: string, projectKey: string }} params
 * @returns {object}
 */
export function createDefaultFloorPlan({ baseKey, projectKey }) {
  const empty = createEmptyFloorPlan({ baseKey, projectKey });
  return addFloor(empty);
}

/**
 * hasAnyRooms
 *
 * @param {object|null|undefined} layout
 * @returns {boolean}
 */
export function hasAnyRooms(layout) {
  const floors = Array.isArray(layout?.floors) ? layout.floors : [];
  return floors.some((floor) => Array.isArray(floor?.rooms) && floor.rooms.length > 0);
}

/**
 * getFloorById
 *
 * @param {object|null|undefined} layout
 * @param {string|null|undefined} floorId
 * @returns {object|null}
 */
export function getFloorById(layout, floorId) {
  const id = String(floorId || '').trim();
  if (!id) return null;
  const floors = Array.isArray(layout?.floors) ? layout.floors : [];
  return floors.find((floor) => floor.id === id) || null;
}

/**
 * suggestFloorName
 *
 * @param {object|null|undefined} layout
 * @returns {string}
 */
export function suggestFloorName(layout) {
  const count = Array.isArray(layout?.floors) ? layout.floors.length : 0;
  if (count < FLOOR_NAME_SEEDS.length) return FLOOR_NAME_SEEDS[count];
  return `Floor ${count + 1}`;
}

/**
 * addFloor
 *
 * @param {object} layout
 * @param {string} [name]
 * @returns {object}
 */
export function addFloor(layout, name) {
  const next = cloneLayout(layout);
  const floor = {
    id: createLayoutId('fl'),
    name: String(name || suggestFloorName(next)).trim() || suggestFloorName(next),
    sortOrder: next.floors.length,
    rooms: [],
  };
  next.floors = [...next.floors, floor];
  return next;
}

/**
 * removeFloor
 *
 * @param {object} layout
 * @param {string} floorId
 * @returns {object}
 */
export function removeFloor(layout, floorId) {
  const id = String(floorId || '').trim();
  const next = cloneLayout(layout);
  next.floors = next.floors
    .filter((floor) => floor.id !== id)
    .map((floor, index) => ({ ...floor, sortOrder: index }));
  return next;
}

/**
 * renameFloor
 *
 * @param {object} layout
 * @param {string} floorId
 * @param {string} name
 * @returns {object}
 */
export function renameFloor(layout, floorId, name) {
  const id = String(floorId || '').trim();
  const nextName = String(name || '').trim();
  if (!id || !nextName) return layout;
  const next = cloneLayout(layout);
  next.floors = next.floors.map((floor) => (
    floor.id === id ? { ...floor, name: nextName } : floor
  ));
  return next;
}

/**
 * addRoom
 *
 * @param {object} layout
 * @param {string} floorId
 * @param {{ x?: number, y?: number, name?: string }} [opts]
 * @returns {{ layout: object, roomId: string|null }}
 */
export function addRoom(layout, floorId, opts = {}) {
  const id = String(floorId || '').trim();
  const next = cloneLayout(layout);
  const floorIndex = next.floors.findIndex((floor) => floor.id === id);
  if (floorIndex < 0) return { layout: next, roomId: null };

  const floor = next.floors[floorIndex];
  const roomCount = floor.rooms.length;
  const room = {
    id: createLayoutId('rm'),
    name: String(opts.name || `Room ${roomCount + 1}`).trim() || `Room ${roomCount + 1}`,
    x: clampNumber(opts.x, 16, next.canvasWidth - DEFAULT_ROOM_WIDTH),
    y: clampNumber(opts.y, 16, next.canvasHeight - DEFAULT_ROOM_HEIGHT),
    width: DEFAULT_ROOM_WIDTH,
    height: DEFAULT_ROOM_HEIGHT,
  };

  next.floors[floorIndex] = {
    ...floor,
    rooms: [...floor.rooms, room],
  };

  return { layout: next, roomId: room.id };
}

/**
 * removeRoom
 *
 * @param {object} layout
 * @param {string} roomId
 * @returns {object}
 */
export function removeRoom(layout, roomId) {
  const id = String(roomId || '').trim();
  if (!id) return layout;
  const next = cloneLayout(layout);
  next.floors = next.floors.map((floor) => ({
    ...floor,
    rooms: floor.rooms.filter((room) => room.id !== id),
  }));
  return next;
}

/**
 * renameRoom
 *
 * @param {object} layout
 * @param {string} roomId
 * @param {string} name
 * @returns {object}
 */
export function renameRoom(layout, roomId, name) {
  const id = String(roomId || '').trim();
  const nextName = String(name || '').trim();
  if (!id || !nextName) return layout;
  return updateRoom(layout, id, (room) => ({ ...room, name: nextName }));
}

/**
 * updateRoomGeometry
 *
 * @param {object} layout
 * @param {string} roomId
 * @param {{ x?: number, y?: number, width?: number, height?: number }} geometry
 * @returns {object}
 */
export function updateRoomGeometry(layout, roomId, geometry = {}) {
  const id = String(roomId || '').trim();
  if (!id) return layout;
  const canvasWidth = Number(layout?.canvasWidth) || DEFAULT_CANVAS_WIDTH;
  const canvasHeight = Number(layout?.canvasHeight) || DEFAULT_CANVAS_HEIGHT;

  return updateRoom(layout, id, (room) => {
    const width = Math.max(60, Number(geometry.width ?? room.width) || DEFAULT_ROOM_WIDTH);
    const height = Math.max(48, Number(geometry.height ?? room.height) || DEFAULT_ROOM_HEIGHT);
    const x = clampNumber(geometry.x ?? room.x, 0, Math.max(0, canvasWidth - width));
    const y = clampNumber(geometry.y ?? room.y, 0, Math.max(0, canvasHeight - height));
    return {
      ...room,
      x,
      y,
      width,
      height,
    };
  });
}

/**
 * toPersistableFloorPlan
 *
 * Strips client-only fields for Firestore write.
 *
 * @param {object} layout
 * @returns {object}
 */
export function toPersistableFloorPlan(layout) {
  const normalized = normalizeFloorPlan(layout);
  return {
    key: normalized.projectKey,
    baseKey: normalized.baseKey,
    projectKey: normalized.projectKey,
    version: PROJECT_FLOOR_PLAN_VERSION,
    canvasWidth: normalized.canvasWidth,
    canvasHeight: normalized.canvasHeight,
    floors: normalized.floors.map((floor, index) => ({
      id: floor.id,
      name: floor.name,
      sortOrder: index,
      rooms: floor.rooms.map((room) => ({
        id: room.id,
        name: room.name,
        x: room.x,
        y: room.y,
        width: room.width,
        height: room.height,
      })),
    })),
  };
}

/**
 * normalizeFloor
 *
 * @param {object} floor
 * @param {number} index
 * @returns {object|null}
 */
function normalizeFloor(floor, index) {
  if (!floor || typeof floor !== 'object') return null;
  const id = String(floor.id || '').trim() || createLayoutId('fl');
  const roomsIn = Array.isArray(floor.rooms) ? floor.rooms : [];
  return {
    id,
    name: String(floor.name || `Floor ${index + 1}`).trim() || `Floor ${index + 1}`,
    sortOrder: Number.isFinite(Number(floor.sortOrder)) ? Number(floor.sortOrder) : index,
    rooms: roomsIn.map((room, roomIndex) => normalizeRoom(room, roomIndex)).filter(Boolean),
  };
}

/**
 * normalizeRoom
 *
 * @param {object} room
 * @param {number} index
 * @returns {object|null}
 */
function normalizeRoom(room, index) {
  if (!room || typeof room !== 'object') return null;
  return {
    id: String(room.id || '').trim() || createLayoutId('rm'),
    name: String(room.name || `Room ${index + 1}`).trim() || `Room ${index + 1}`,
    x: Number(room.x) || 0,
    y: Number(room.y) || 0,
    width: Math.max(60, Number(room.width) || DEFAULT_ROOM_WIDTH),
    height: Math.max(48, Number(room.height) || DEFAULT_ROOM_HEIGHT),
  };
}

/**
 * cloneLayout
 *
 * @param {object} layout
 * @returns {object}
 */
function cloneLayout(layout) {
  const source = layout || {};
  const cloned = typeof structuredClone === 'function'
    ? structuredClone(source)
    : JSON.parse(JSON.stringify(source));
  return normalizeFloorPlan(cloned);
}

/**
 * updateRoom
 *
 * @param {object} layout
 * @param {string} roomId
 * @param {(room: object) => object} updater
 * @returns {object}
 */
function updateRoom(layout, roomId, updater) {
  const next = cloneLayout(layout);
  next.floors = next.floors.map((floor) => ({
    ...floor,
    rooms: floor.rooms.map((room) => (room.id === roomId ? updater(room) : room)),
  }));
  return next;
}

/**
 * clampNumber
 *
 * @param {unknown} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clampNumber(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}
